import { GameEngine } from './gameEngine.ts';
import { Camera } from '../render/camera.ts';
import { HudManager } from '../ui/hudManager.ts';

export class PromoDirector {
  private engine: GameEngine;
  private camera: Camera;
  private hudManager: HudManager;
  private isRunning = false;
  private startTime = 0;
  private intervalId: number | null = null;

  constructor(engine: GameEngine, camera: Camera, hudManager: HudManager) {
    this.engine = engine;
    this.camera = camera;
    this.hudManager = hudManager;
  }

  /**
   * Bắt đầu kịch bản mô phỏng đạo diễn tự động 30s (tỉ lệ 9:16)
   */
  public start(): void {
    if (this.isRunning) return;
    this.isRunning = true;
    (window as any).__PROMO_RUNNING = true;
    (window as any).__PROMO_FINISHED = false;

    // 1. Tự động ẩn Splash & Lobby
    const splash = document.getElementById('splash-screen');
    const lobby = document.getElementById('lobby-modal');
    if (splash) splash.style.display = 'none';
    if (lobby) lobby.style.display = 'none';

    // 2. Nạp map mặc định Cầu Giấy với nhân vật Dũng Corgi
    this.engine.loadMap('cau-giay', 'corgi', 'Dũng Corgi');
    this.camera.setMaxRadius(120);
    this.camera.zoom = 2.4; // Tỉ lệ zoom lý tưởng cho màn hình dọc 9:16

    // 3. Khởi tạo trạng thái ban đầu
    const state = this.engine.getState();
    state.dog.bowels = 35;
    this.engine.setParanoia(15);
    this.engine.setReputation(100);
    this.camera.worldX = state.player.x;
    this.camera.worldY = state.player.y;

    this.startTime = performance.now();
    this.hudManager.showGameMasterBar();

    // 4. Bắt đầu timeline điều khiển 30s (60 ticks / giây)
    let lastSecond = -1;
    this.intervalId = window.setInterval(() => {
      const elapsedMs = performance.now() - this.startTime;
      const elapsedSec = elapsedMs / 1000;
      const curSecInt = Math.floor(elapsedSec);

      if (curSecInt !== lastSecond) {
        lastSecond = curSecInt;
        console.log(`[PromoDirector 30s] Giây: ${curSecInt}s / 30s`);
      }

      this.tick(elapsedSec);

      if (elapsedSec >= 30) {
        this.stop();
      }
    }, 16);
  }

  public stop(): void {
    if (!this.isRunning) return;
    this.isRunning = false;
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    (window as any).__PROMO_FINISHED = true;
    console.log('[PromoDirector] Đã hoàn thành toàn bộ kịch bản 30 giây!');
  }

  private tick(sec: number): void {
    const state = this.engine.getState();

    // Luôn follow camera bám sát người chơi
    this.camera.worldX += (state.player.x - this.camera.worldX) * 0.15;
    this.camera.worldY += (state.player.y - this.camera.worldY) * 0.15;

    // ==========================================
    // PHASE 1: 0.0s - 4.5s [DẮT CHÓ DẠO PHỐ BÌNH YÊN]
    // ==========================================
    if (sec < 4.5) {
      // Tự động di chuyển sang phải/ngã ba dọc đường
      this.engine.setPlayerTarget({ x: 18, y: 0 });
      state.dog.bowels = Math.min(96, 35 + sec * 14);

      if (sec < 2.0) {
        this.engine.setGameMasterComment(
          'DẠO PHỐ 9:16',
          '🐾 [01/05] Buổi sáng bình yên: Dắt Corgi dạo ngõ phố Hà Nội...',
          2500
        );
      } else {
        this.engine.setGameMasterComment(
          'CẢNH BÁO RUỘT',
          '⚠️ Ruột Corgi căng 95%! Dấu hiệu sắp xả bãi mìn vỉa hè!',
          2500
        );
      }
    }

    // ==========================================
    // PHASE 2: 4.5s - 9.0s [CÚN XẢ MÌN & LƯỠNG NAN 3S]
    // ==========================================
    else if (sec >= 4.5 && sec < 9.0) {
      if (sec < 5.0 && (!state.activeDilemma || !state.activeDilemma.active)) {
        // Cún mót đầy 100% -> xả bãi phân
        state.dog.bowels = 100;
        this.engine.setPlayerTarget({ x: state.player.x, y: state.player.y }); // Dừng lại
      }

      if (sec >= 5.0 && sec < 8.5) {
        this.engine.setGameMasterComment(
          'LƯỠNG NAN VỈA HÈ',
          '🚨 [02/05] BÃI MÌN XUẤT HIỆN! Dọn sạch (tốn túi) hay Chuồn lẹ (chi phí 0)?',
          3500
        );
      }
    }

    // ==========================================
    // PHASE 3: 9.0s - 14.0s [QUYẾT ĐỊNH: CHUỒN LẸ (DEFECT)]
    // ==========================================
    else if (sec >= 9.0 && sec < 14.0) {
      if (sec < 9.2 && state.activeDilemma && state.activeDilemma.active) {
        // Tự động bấm nút Chuồn Lẹ
        const btnDefect = document.getElementById('btnDefect');
        if (btnDefect) {
          btnDefect.click();
        }
      }

      // Người chơi bấm điện thoại bỏ chạy sang góc ngõ
      this.engine.setPlayerTarget({ x: -15, y: 12 });

      if (sec >= 9.5 && sec < 12.0) {
        this.engine.setGameMasterComment(
          'FREE-RIDING',
          '💨 [03/05] LỰA CHỌN PHẢN BỘI: Giả vờ bấm điện thoại chuồn lẹ!',
          3500
        );
      }
    }

    // ==========================================
    // PHASE 4: 14.0s - 20.0s [CHIBI BÀ TÁM RỌI ĐÈN TRI HÔ]
    // ==========================================
    else if (sec >= 14.0 && sec < 20.0) {
      // Tìm hoặc tạo một NPC Chibi Bà Tám ngay gần ngã ba
      let baTam = state.dummyWalkers.find((w) => w.archetype === 'ba_tam');
      if (!baTam && state.dummyWalkers.length > 0) {
        baTam = state.dummyWalkers[0];
        baTam.archetype = 'ba_tam';
        baTam.name = 'Bà Tám Đầu Ngõ';
      }

      if (baTam) {
        baTam.x = state.player.x + 8;
        baTam.y = state.player.y - 4;
        baTam.hasFlashlight = true;
        baTam.isShouting = true;
        baTam.shoutText = 'ỐI LÀNG NƯỚC ƠI BẮT QUẢ TANG XẢ BẬY NÀY!';
        baTam.facingAngle = Math.atan2(state.player.y - baTam.y, state.player.x - baTam.x);
      }

      // Kích hoạt sóng âm tri hô báo động
      if (sec >= 14.5 && state.soundWaves.length === 0) {
        this.engine.triggerShoutAlarm(
          state.player.x + 8,
          state.player.y - 4,
          'ỐI LÀNG NƯỚC ƠI BẮT QUẢ TANG XẢ BẬY NÀY!',
          25
        );
      }

      // Bác Dân Phòng chạy ùa tới
      const danPhong = state.dummyWalkers.find((w) => w.archetype === 'dan_phong');
      if (danPhong) {
        danPhong.isRushingToScene = true;
        danPhong.x += (state.player.x - danPhong.x) * 0.08;
        danPhong.y += (state.player.y - danPhong.y) * 0.08;
      }

      this.engine.setParanoia(Math.min(100, state.paranoia + 1.5));

      if (sec >= 15.0 && sec < 18.0) {
        this.engine.setGameMasterComment(
          'BẮT QUẢ TANG',
          '📢 [04/05] BÀ TÁM TRI HÔ: Sóng âm báo động lan tỏa 25m! Dân phòng ùa tới!',
          4000
        );
      }
    }

    // ==========================================
    // PHASE 5: 20.0s - 26.0s [MODAL VIRAL BÓC PHỐT CƯ DÂN ZALO]
    // ==========================================
    else if (sec >= 20.0 && sec < 26.0) {
      const viralModal = document.getElementById('viralModal');
      if (viralModal && viralModal.style.display !== 'flex') {
        // Tự động kích hoạt màn hình bóc phốt
        this.engine.triggerViralBusted(
          'Camera an ninh Kim Tài ghi lại cảnh dắt chó xả bậy trước cửa nhà rồi giả vờ bấm điện thoại chuồn lẹ!',
          'Camera & Bà Tám Đầu Ngõ'
        );
      }

      if (sec >= 20.5 && sec < 24.0) {
        this.engine.setGameMasterComment(
          'BÓC PHỐT CƯ DÂN',
          '🔥 [05/05] HỆ SỐ NHỤC NHÃ x3.5: Trò chơi lặp lại trừng phạt kẻ bội phản!',
          4000
        );
      }
    }

    // ==========================================
    // PHASE 6: 26.0s - 30.0s [OUTRO CTA QUẢNG BÁ GAME]
    // ==========================================
    else if (sec >= 26.0) {
      const viralModal = document.getElementById('viralModal');
      if (viralModal) viralModal.style.display = 'none';

      const promoOutroModal = document.getElementById('promoOutroModal');
      if (promoOutroModal) {
        promoOutroModal.style.display = 'flex';
      }

      this.engine.setGameMasterComment(
        'THE DOG POOP DILEMMA',
        '🌐 Chơi ngay trên trình duyệt: dog-poop-dilemma.pages.dev',
        4000
      );
    }
  }
}
